/**
 * Workflow utility functions
 * 
 * @fileoverview Utility functions for workflow operations
 */

/**
 * Check if two data types are compatible
 * @param {string} sourceType - Source data type
 * @param {string} targetType - Target data type
 * @returns {boolean} True if compatible
 */
export function isCompatibleType(sourceType, targetType) {
  // Any type can connect to any
  if (sourceType === 'any' || targetType === 'any') {
    return true;
  }

  // Same types are compatible
  if (sourceType === targetType) {
    return true;
  }

  // Object can accept any object-like type
  if (targetType === 'object' && ['object', 'json'].includes(sourceType)) {
    return true;
  }

  // Array can accept any array-like type
  if (targetType === 'array' && ['array', 'list'].includes(sourceType)) {
    return true;
  }

  // String can accept text-like types
  if (targetType === 'string' && ['string', 'text'].includes(sourceType)) {
    return true;
  }

  return false;
}

/**
 * Check if connection would create a cycle
 * @param {Object[]} nodes - Array of nodes
 * @param {Object[]} edges - Array of edges
 * @param {Object} newEdge - New edge to add
 * @returns {boolean} True if cycle would be created
 */
export function wouldCreateCycle(nodes, edges, newEdge) {
  // Build adjacency list
  const graph = new Map();
  nodes.forEach((node) => {
    graph.set(node.id, []);
  });

  edges.forEach((edge) => {
    if (!graph.has(edge.source)) {
      graph.set(edge.source, []);
    }
    graph.get(edge.source).push(edge.target);
  });

  // Add new edge
  if (!graph.has(newEdge.source)) {
    graph.set(newEdge.source, []);
  }
  graph.get(newEdge.source).push(newEdge.target);

  // DFS to detect cycle
  const visited = new Set();
  const recStack = new Set();

  /**
   * DFS helper
   * @param {string} nodeId
   * @returns {boolean}
   */
  function hasCycle(nodeId) {
    if (recStack.has(nodeId)) {
      return true;
    }
    if (visited.has(nodeId)) {
      return false;
    }

    visited.add(nodeId);
    recStack.add(nodeId);

    const neighbors = graph.get(nodeId) || [];
    for (const neighbor of neighbors) {
      if (hasCycle(neighbor)) {
        return true;
      }
    }

    recStack.delete(nodeId);
    return false;
  }

  // Check all nodes
  for (const nodeId of graph.keys()) {
    if (!visited.has(nodeId)) {
      if (hasCycle(nodeId)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Topological sort of nodes
 * @param {Object[]} nodes - Array of nodes
 * @param {Object[]} edges - Array of edges
 * @returns {string[]} Node IDs in execution order
 */
export function topologicalSort(nodes, edges) {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const inDegree = new Map(nodes.map((n) => [n.id, 0]));
  const graph = new Map(nodes.map((n) => [n.id, []]));

  // Build graph and calculate in-degrees
  edges.forEach((edge) => {
    if (graph.has(edge.source) && graph.has(edge.target)) {
      graph.get(edge.source).push(edge.target);
      inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
    }
  });

  // Find nodes with no incoming edges
  const queue = [];
  inDegree.forEach((degree, nodeId) => {
    if (degree === 0) {
      queue.push(nodeId);
    }
  });

  const result = [];

  // Process nodes
  while (queue.length > 0) {
    const nodeId = queue.shift();
    result.push(nodeId);

    const neighbors = graph.get(nodeId) || [];
    neighbors.forEach((neighborId) => {
      const newDegree = (inDegree.get(neighborId) || 0) - 1;
      inDegree.set(neighborId, newDegree);
      if (newDegree === 0) {
        queue.push(neighborId);
      }
    });
  }

  // Check for cycles
  if (result.length !== nodes.length) {
    throw new Error('Workflow contains cycles or disconnected nodes');
  }

  return result;
}

/**
 * Collect inputs for a node from connected nodes
 * @param {Object} node - Current node
 * @param {Object[]} edges - All edges
 * @param {Object} results - Results from previous nodes
 * @returns {Object} Collected inputs
 */
export function collectNodeInputs(node, edges, results) {
  const inputs = {};

  // Find incoming edges
  const incomingEdges = edges.filter((e) => e.target === node.id);

  incomingEdges.forEach((edge) => {
    const sourceResult = results[edge.source];
    if (sourceResult) {
      // Merge source result into inputs
      Object.assign(inputs, sourceResult);
    }
  });

  // Merge with node's static parameters
  if (node.data && node.data.parameters) {
    Object.assign(inputs, node.data.parameters);
  }

  return inputs;
}

/**
 * Validate connection
 * @param {Object} connection - Connection object
 * @param {Object[]} nodes - Array of nodes
 * @param {Object[]} edges - Array of edges
 * @returns {{valid: boolean, error?: string}}
 */
export function validateConnection(connection, nodes, edges) {
  const sourceNode = nodes.find((n) => n.id === connection.source);
  const targetNode = nodes.find((n) => n.id === connection.target);

  if (!sourceNode || !targetNode) {
    return { valid: false, error: 'Source or target node not found' };
  }

  // Check for self-connection
  if (connection.source === connection.target) {
    return { valid: false, error: 'Cannot connect node to itself' };
  }

  // Check for duplicate connection
  const duplicate = edges.find(
    (e) =>
      e.source === connection.source &&
      e.target === connection.target &&
      e.sourceHandle === connection.sourceHandle &&
      e.targetHandle === connection.targetHandle
  );
  if (duplicate) {
    return { valid: false, error: 'Connection already exists' };
  }

  // Check for cycles
  if (wouldCreateCycle(nodes, edges, connection)) {
    return { valid: false, error: 'Cannot create circular dependency' };
  }

  return { valid: true };
}

